/**
 * Report Presets — saved report definitions that can be edited in place or
 * cloned into a one-off/scheduled order.
 *
 * The list previously showed every preset in the account, including other users'
 * private ones, and offered Delete on all of them. The API now returns only the
 * caller's own presets, ones flagged shared, and ownerless ones, and gates edit
 * and delete via `canManage`.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn, type DataTableAction } from '@/components/ui/DataTable';
import { customReportsApi } from '@/services/customReports.service';
import { getErrorMessage } from '@/services/apiClient';
import type { ReportPreset } from '@/types/customReports.types';

const QUERY_KEY = ['reports', 'presets'] as const;

/** Rows without the flag predate the API change; treat them as manageable. */
const canManage = (row: ReportPreset) => row.canManage !== false;

export default function ReportPresetsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteItem, setDeleteItem] = useState<ReportPreset | null>(null);
  const [search, setSearch] = useState('');

  const presets = useQuery({
    queryKey: QUERY_KEY,
    queryFn: customReportsApi.presets.list,
    staleTime: 30_000,
  });

  // Client-side: the endpoint returns the caller's visible presets in one
  // unpaginated response, so there is no query to push a term into.
  //
  // `reportSelect` is in the haystack, not just the title. It is the report a
  // preset is built on and it is already rendered under the title — an account
  // with a dozen presets named after the person who saved them is searchable by
  // what they actually produce.
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = presets.data ?? [];
    if (!term) return all;

    return all.filter((row) =>
      `${row.title ?? ''} ${row.reportSelect ?? ''}`.toLowerCase().includes(term),
    );
  }, [presets.data, search]);

  const invalidate = () => qc.invalidateQueries({ queryKey: QUERY_KEY });
  const onError = (err: unknown) => setActionError(getErrorMessage(err, 'Action failed'));

  const remove = useMutation({
    mutationFn: (id: number) => customReportsApi.presets.remove(id),
    onSuccess: () => {
      setDeleteItem(null);
      setActionError(null);
      invalidate();
    },
    onError: (err) => {
      setDeleteItem(null);
      onError(err);
    },
  });

  const columns: DataTableColumn<ReportPreset>[] = [
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: (row) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900">{row.title || '(untitled)'}</span>
            {row.unassigned && (
              <span title="No owner on record — anyone in the account can edit this">
                <Badge variant="warning" size="sm">
                  Unassigned
                </Badge>
              </span>
            )}
            {row.sharedWithMe && (
              <Badge variant="neutral" size="sm">
                Shared with you
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500">{row.reportSelect}</div>
        </div>
      ),
    },
    {
      key: 'shared',
      header: 'Shared',
      width: 'w-[12%]',
      align: 'center',
      render: (row) => (row.shared ? 'Yes' : 'No'),
    },
    {
      key: 'dateCreated',
      header: 'Created',
      width: 'w-[18%]',
      render: (row) => (row.dateCreated ? new Date(row.dateCreated).toLocaleDateString() : '—'),
    },
  ];

  const actions: DataTableAction<ReportPreset>[] = [
    {
      label: 'Edit',
      onClick: (row) => navigate(`/reports/presets/${row.reportsCustomPresetID}/edit`),
      hidden: (row) => !canManage(row),
    },
    // Both open the wizard pre-filled from the preset and create nothing until the
    // user submits. They used to POST a clone first: the on-demand target wrote a
    // reports_cdm_orders row and then sent the user to a URL the wizard resolves
    // against reports_cst_orders, so the settings appeared not to carry over at all;
    // the scheduled target created the order outright and returned to the list, with
    // no chance to set the date range it was going to run for.
    {
      label: 'Use as on-demand',
      onClick: (row) =>
        navigate(`/reports/presets/${row.reportsCustomPresetID}/use?target=on-demand`),
    },
    {
      label: 'Use as scheduled',
      onClick: (row) =>
        navigate(`/reports/presets/${row.reportsCustomPresetID}/use?target=scheduled`),
    },
    {
      label: 'Delete',
      variant: 'danger',
      onClick: (row) => setDeleteItem(row),
      // Delete used to render on every row, so any user could destroy a
      // colleague's private preset.
      hidden: (row) => !canManage(row),
    },
  ];

  return (
    // Viewport-height shell, matching every other list page: the header stays
    // put and the list is the only thing that scrolls. This page used to be a
    // bare `p-6` block, so a long preset list scrolled the whole document and
    // took the heading with it.
    <div className="flex h-full min-h-0 flex-col overflow-x-hidden max-md:h-auto">
      <div className="shrink-0 p-6">
        <PageHeader variant="compact" title="Report Presets" />

        <div className="mt-4 flex items-center gap-3">
          {/* `leftAddon`, not an icon positioned by this page against its own
              wrapper: Input renders its field inside a `relative` div of its own
              that comes AFTER such an icon in the DOM, and `form-input-base`
              carries `bg-white` — so an outside icon paints underneath the field
              and is invisible. leftAddon puts it in that same wrapper and adds
              the `pl-9` itself. */}
          <div className="max-w-sm flex-1">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search presets by name or report…"
              leftAddon={<Search className="h-4 w-4" />}
            />
          </div>
        </div>

        {actionError && (
          <Alert variant="error" className="mt-4">
            {actionError}
          </Alert>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
        <DataTable<ReportPreset>
          data={rows}
          columns={columns}
          keyField="reportsCustomPresetID"
          rowActions={actions}
          loading={presets.isLoading}
          tableFixed
          className="[&>div]:overflow-x-hidden"
          emptyTitle={search.trim() ? 'No matching presets' : 'No presets saved'}
          emptyDescription={
            search.trim()
              ? `Nothing matches "${search.trim()}". Try a different name or report.`
              : 'Save a report definition as a preset to re-run it or spawn new orders from it.'
          }
        />
      </div>

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete preset?"
        message={
          deleteItem
            ? `Delete "${deleteItem.title || '(untitled)'}"? Reports already created from it are not affected.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deleteItem) remove.mutate(deleteItem.reportsCustomPresetID);
        }}
        onClose={() => setDeleteItem(null)}
      />
    </div>
  );
}
